import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import AdminHeader from '../components/AdminHeader';
import AdminTabs from '../components/AdminTabs';
import MarketingLinks from '../components/MarketingLinks';
import Toast from '../components/Toast';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { API_BASE_URL } from '../config/api';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  post: (url, data, config = {}) => axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  put: (url, data, config = {}) => axios.put(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  delete: (url, config = {}) => axios.delete(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

const hasHtmlContent = (html) => {
  if (!html || !String(html).trim()) return false;
  const text = String(html).replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
  return text.length > 0;
};

function ContactInquiriesPage() {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [filteredInquiries, setFilteredInquiries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('new');
  const [toast, setToast] = useState(null);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [inquiryToDelete, setInquiryToDelete] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyInquiry, setReplyInquiry] = useState(null);
  const [replySubject, setReplySubject] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const [showLogModal, setShowLogModal] = useState(false);
  const [logInquiry, setLogInquiry] = useState(null);
  const [replyLog, setReplyLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [viewingReply, setViewingReply] = useState(null);

  useEffect(() => {
    document.title = 'Contact Inquiries - Design Store Admin';
    fetchInquiries();
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await authAxios.get(`${API_BASE_URL}/admin/outreach/templates`);
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  useEffect(() => {
    filterInquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterInquiries filters inquiries
  }, [inquiries, statusFilter]);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/contact/inquiries`);
      if (response.data.success) {
        setInquiries(response.data.data);
      } else {
        setError('Failed to fetch inquiries');
      }
    } catch (err) {
      console.error('Error fetching inquiries:', err);
      setError(err.response?.data?.error || 'Failed to fetch inquiries');
    } finally {
      setLoading(false);
    }
  };

  const filterInquiries = () => {
    if (statusFilter === 'all') {
      setFilteredInquiries(inquiries);
    } else {
      setFilteredInquiries(inquiries.filter(inquiry => inquiry.status === statusFilter));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'read':
        return 'bg-yellow-100 text-yellow-800';
      case 'replied':
        return 'bg-green-100 text-green-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStatusChange = async (inquiryId, newStatus) => {
    try {
      const response = await authAxios.put(`${API_BASE_URL}/admin/contact/inquiries/${inquiryId}`, {
        status: newStatus
      });

      if (response.data.success) {
        setInquiries(inquiries.map(inq => 
          inq.id === inquiryId ? response.data.data : inq
        ));
        setToast({ message: 'Status updated successfully', type: 'success' });
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setToast({ message: err.response?.data?.error || 'Failed to update status', type: 'error' });
    }
  };

  const handleViewDetails = (inquiry) => {
    setSelectedInquiry(inquiry);
    setAdminNotes(inquiry.adminNotes || '');
    setShowDetails(true);
  };

  const handleSaveNotes = async () => {
    if (!selectedInquiry) return;

    try {
      const response = await authAxios.put(`${API_BASE_URL}/admin/contact/inquiries/${selectedInquiry.id}`, {
        adminNotes: adminNotes
      });

      if (response.data.success) {
        setInquiries(inquiries.map(inq => 
          inq.id === selectedInquiry.id ? response.data.data : inq
        ));
        setSelectedInquiry(response.data.data);
        setToast({ message: 'Notes saved successfully', type: 'success' });
      }
    } catch (err) {
      console.error('Error saving notes:', err);
      setToast({ message: err.response?.data?.error || 'Failed to save notes', type: 'error' });
    }
  };

  const handleDeleteClick = (inquiry) => {
    setInquiryToDelete(inquiry);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!inquiryToDelete) return;

    try {
      const response = await authAxios.delete(`${API_BASE_URL}/admin/contact/inquiries/${inquiryToDelete.id}`);

      if (response.data.success) {
        setInquiries(inquiries.filter(inq => inq.id !== inquiryToDelete.id));
        setToast({ message: 'Inquiry deleted successfully', type: 'success' });
        setShowDeleteConfirm(false);
        setInquiryToDelete(null);
      }
    } catch (err) {
      console.error('Error deleting inquiry:', err);
      setToast({ message: err.response?.data?.error || 'Failed to delete inquiry', type: 'error' });
    }
  };

  const handleOpenReply = (inquiry) => {
    setReplyInquiry(inquiry);
    setReplySubject('');
    setReplyMessage('');
    setSelectedTemplateId('');
    setShowReplyModal(true);
  };

  const handleSelectTemplate = (e) => {
    const id = e.target.value;
    setSelectedTemplateId(id);
    const t = templates.find(x => x.id === id);
    if (t) {
      setReplySubject(t.subject || '');
      setReplyMessage(t.message || '');
    }
  };

  const handleSendReply = async () => {
    if (!replyInquiry || !replySubject.trim() || !replyMessage.trim()) {
      setToast({ message: 'Subject and message are required', type: 'error' });
      return;
    }
    if (!hasHtmlContent(replyMessage)) {
      setToast({ message: 'Message is required', type: 'error' });
      return;
    }
    setReplySending(true);
    try {
      const response = await authAxios.post(
        `${API_BASE_URL}/admin/contact/inquiries/${replyInquiry.id}/reply`,
        { subject: replySubject.trim(), message: replyMessage.trim() }
      );
      if (response.data.success) {
        setInquiries(inquiries.map(inq => inq.id === replyInquiry.id ? { ...inq, status: 'replied' } : inq));
        setToast({ message: 'Reply sent successfully', type: 'success' });
        setShowReplyModal(false);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to send reply', type: 'error' });
    } finally {
      setReplySending(false);
    }
  };

  const handleOpenLog = async (inquiry) => {
    setLogInquiry(inquiry);
    setShowLogModal(true);
    setLogLoading(true);
    try {
      const response = await authAxios.get(`${API_BASE_URL}/admin/contact/inquiries/${inquiry.id}/replies`);
      if (response.data.success) {
        setReplyLog(response.data.data);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to load reply log', type: 'error' });
    } finally {
      setLogLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <AdminHeader />
          <AdminTabs />
          <MarketingLinks />
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading inquiries...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <AdminHeader />
          <AdminTabs />
          <MarketingLinks />
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button
              onClick={fetchInquiries}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <AdminHeader />
        <AdminTabs />
        <MarketingLinks />

        <div className="mb-6 flex flex-wrap items-end gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Contact Inquiries</h2>
            <p className="text-sm text-gray-600">View inquiries and reply via email with templates.</p>
          </div>
          <div className="flex-1" />
          <button
            onClick={() => navigate('/admin', { state: { activeTab: 'outreach-email' } })}
            className="px-4 py-2 text-blue-600 hover:text-blue-800 text-sm"
          >
            Outreach Email Settings
          </button>
        </div>

        <div className="mb-4">
          {/* Status Filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Status:
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
              <option value="archived">Archived</option>
              <option value="all">All Statuses</option>
            </select>
          </div>

          <div className="text-sm text-gray-600 mb-4">
            Showing {filteredInquiries.length} of {inquiries.length} inquiries
          </div>
        </div>

        {/* Inquiries List */}
        {filteredInquiries.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-600">No inquiries found with status "{statusFilter}"</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInquiries.map((inquiry) => (
                    <tr key={inquiry.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(inquiry.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {inquiry.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <a href={`mailto:${inquiry.email}`} className="text-blue-600 hover:text-blue-800">
                          {inquiry.email}
                        </a>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="max-w-xs truncate" title={inquiry.subject}>
                          {inquiry.subject}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={inquiry.status}
                          onChange={(e) => handleStatusChange(inquiry.id, e.target.value)}
                          className={`px-2 py-1 text-xs leading-5 font-semibold rounded-full border-0 ${getStatusColor(inquiry.status)}`}
                        >
                          <option value="new">New</option>
                          <option value="read">Read</option>
                          <option value="replied">Replied</option>
                          <option value="archived">Archived</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-0">
                          <button onClick={() => handleViewDetails(inquiry)} title="View" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                          </button>
                          <button onClick={() => handleOpenReply(inquiry)} title="Reply" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                          </button>
                          <button onClick={() => handleOpenLog(inquiry)} title="View Log" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                          </button>
                          <button onClick={() => handleDeleteClick(inquiry)} title="Delete" className="p-1 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {showDetails && selectedInquiry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Inquiry Details</h3>
                <div className="flex items-center gap-0">
                  <button onClick={() => { setShowDetails(false); handleOpenReply(selectedInquiry); }} title="Reply" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  </button>
                  <button onClick={() => { setShowDetails(false); handleOpenLog(selectedInquiry); }} title="View Log" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                  </button>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedInquiry.name}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">
                    <a href={`mailto:${selectedInquiry.email}`} className="text-blue-600 hover:text-blue-800">
                      {selectedInquiry.email}
                    </a>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Subject</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedInquiry.subject}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Message</label>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedInquiry.message}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Date</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(selectedInquiry.createdAt)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notes</label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add notes about this inquiry..."
                  />
                  <button
                    onClick={handleSaveNotes}
                    className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Save Notes
                  </button>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reply Modal */}
        {showReplyModal && replyInquiry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-2">Reply to {replyInquiry.name}</h3>
              <p className="text-sm text-gray-500 mb-4">To: {replyInquiry.email}</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                <select
                  value={selectedTemplateId}
                  onChange={handleSelectTemplate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">— Select template —</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Reply subject"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={replyMessage}
                    onChange={setReplyMessage}
                    modules={QUILL_MODULES}
                    placeholder="Compose your reply..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowReplyModal(false)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
                <button onClick={handleSendReply} disabled={replySending || !replySubject.trim() || !hasHtmlContent(replyMessage)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {replySending ? 'Sending...' : 'Send Reply'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reply Log Modal */}
        {showLogModal && logInquiry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-2">Reply Log for {logInquiry.name}</h3>
              {logLoading ? (
                <p className="text-gray-500">Loading...</p>
              ) : replyLog.length === 0 ? (
                <p className="text-gray-500">No replies sent yet.</p>
              ) : (
                <div className="space-y-2">
                  {replyLog.map((e) => (
                    <div
                      key={e.id}
                      className="p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                      onClick={() => setViewingReply(e)}
                    >
                      <div className="font-medium text-sm">{e.subject}</div>
                      <div className="text-gray-500 text-sm">{formatDate(e.sentAt)}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4">
                <button onClick={() => { setShowLogModal(false); setViewingReply(null); }} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* View Reply Modal */}
        {viewingReply && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setViewingReply(null)}>
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-2">Reply Sent {formatDate(viewingReply.sentAt)}</h3>
              <p className="text-sm text-gray-500 mb-2">To: {viewingReply.toEmail}</p>
              <p className="text-sm font-medium mb-1">{viewingReply.subject}</p>
              <div className="border border-gray-200 rounded p-4 bg-gray-50 text-sm prose max-w-none" dangerouslySetInnerHTML={{ __html: viewingReply.message || '' }} />
              <button onClick={() => setViewingReply(null)} className="mt-4 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Close</button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showDeleteConfirm}
          title="Delete Inquiry"
          message={`Are you sure you want to delete the inquiry from ${inquiryToDelete?.name}? This action cannot be undone.`}
          onConfirm={handleDeleteConfirm}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setInquiryToDelete(null);
          }}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
        
        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  );
}

export default ContactInquiriesPage;
